/**
 * Seen-area bitmaps (MSEA per user, MSAT tissue). See morpho/stitching/seen_area.py for the layout:
 * 40-byte header, 16-byte level entries (cols, rows, byteOffset, bitCount), then MSB-first bitsets.
 * The grid comes from the server (`GET /api/slide/<id>/seen_area/`), with the backend field names.
 */

export const MAGIC_SEEN = 0x4145534D; // 'MSEA'
export const MAGIC_TISSUE = 0x5441534D; // 'MSAT'
const VERSION = 1;
const HEADER_SIZE = 40;
const LEVEL_ENTRY_SIZE = 16;
const NO_TILED = 255;

export const STITCHED = 'stitched';
export const TILED = 'tiled';

const GRID_KEYS = ['stitched_x_max', 'stitched_y_max', 'z_max', 'min_level', 'max_level',
    'x_fields', 'y_fields', 'tiled_min', 'tiled_max'];

/** Same string as the backend's Grid.signature(). */
export const gridSignature = (grid) => GRID_KEYS.map(key => grid[key]).join(',');

export const levelKey = (kind, level) => `${kind}:${level}`;

export const hasTiledLevels = (grid) => grid.tiled_min !== NO_TILED;

export const stitchedLevelGrid = (grid, level) => {
    const divider = Math.pow(2, grid.z_max - level);
    return {
        cols: Math.max(1, Math.ceil(grid.stitched_x_max / divider)),
        rows: Math.max(1, Math.ceil(grid.stitched_y_max / divider)),
    };
};

/** {levels: [{kind, level, cols, rows, byteOffset, bitCount}], byKey, byteLength} in file order. */
export const getLevelLayout = (grid) => {
    const dims = [];
    for (let level = grid.min_level; level <= grid.max_level; level++) {
        dims.push({kind: STITCHED, level, ...stitchedLevelGrid(grid, level)});
    }
    if (hasTiledLevels(grid)) {
        for (let level = grid.tiled_min; level <= grid.tiled_max; level++) {
            dims.push({kind: TILED, level, cols: grid.x_fields, rows: grid.y_fields});
        }
    }
    let cursor = HEADER_SIZE + dims.length * LEVEL_ENTRY_SIZE;
    const byKey = {};
    const levels = dims.map(dim => {
        const entry = {...dim, byteOffset: cursor, bitCount: dim.cols * dim.rows};
        cursor += Math.ceil(entry.bitCount / 8);
        byKey[levelKey(dim.kind, dim.level)] = entry;
        return entry;
    });
    return {levels, byKey, byteLength: cursor};
};

/** A zeroed bitmap body laid out like the file, so file bytes can be OR-ed in directly. */
export const emptyBits = (layout) => new Uint8Array(layout.byteLength);

/** @returns {{magic, grid, updatedAtMs, bytes: Uint8Array}|null} */
export const parseSeenAreaFile = (arrayBuffer) => {
    if (!arrayBuffer || arrayBuffer.byteLength < HEADER_SIZE) return null;
    const view = new DataView(arrayBuffer);
    const magic = view.getUint32(0, true);
    if ((magic !== MAGIC_SEEN && magic !== MAGIC_TISSUE) || view.getUint16(4, true) !== VERSION) return null;
    if (view.getUint32(28, true) !== HEADER_SIZE) return null;
    const grid = {
        stitched_x_max: view.getUint32(8, true),
        stitched_y_max: view.getUint32(12, true),
        z_max: view.getUint8(16),
        min_level: view.getUint8(17),
        max_level: view.getUint8(18),
        x_fields: view.getUint16(20, true),
        y_fields: view.getUint16(22, true),
        tiled_min: view.getUint8(24),
        tiled_max: view.getUint8(25),
    };
    if (grid.max_level < grid.min_level) return null;
    const layout = getLevelLayout(grid);
    if (layout.byteLength !== arrayBuffer.byteLength) return null;
    for (let i = 0; i < layout.levels.length; i++) {
        const base = HEADER_SIZE + i * LEVEL_ENTRY_SIZE;
        const entry = layout.levels[i];
        if (view.getUint32(base + 8, true) !== entry.byteOffset || view.getUint32(base + 12, true) !== entry.bitCount) return null;
    }
    return {magic, grid, updatedAtMs: Number(view.getBigUint64(32, true)), bytes: new Uint8Array(arrayBuffer)};
};

export const hasBit = (bytes, entry, idx) =>
    idx >= 0 && idx < entry.bitCount && (bytes[entry.byteOffset + (idx >> 3)] & (1 << (7 - (idx & 7)))) !== 0;

/** Returns true when the bit was newly set. */
export const setBit = (bytes, entry, idx) => {
    if (idx < 0 || idx >= entry.bitCount) return false;
    const byte = entry.byteOffset + (idx >> 3);
    const mask = 1 << (7 - (idx & 7));
    if (bytes[byte] & mask) return false;
    bytes[byte] |= mask;
    return true;
};

/** OR `source` (same layout) into `target`, level bitsets only. */
export const orBits = (target, source, layout) => {
    const start = layout.levels.length ? layout.levels[0].byteOffset : layout.byteLength;
    for (let i = start; i < layout.byteLength; i++) target[i] |= source[i];
};

const POPCOUNT = Uint8Array.from({length: 256}, (_, b) => {
    let n = 0;
    for (let v = b; v; v >>= 1) n += v & 1;
    return n;
});

/** Bits set in one level; with `mask`, only bits also set there. Trailing pad bits are always zero. */
export const countLevelBits = (bytes, entry, mask = null) => {
    const end = entry.byteOffset + Math.ceil(entry.bitCount / 8);
    let count = 0;
    for (let i = entry.byteOffset; i < end; i++) count += POPCOUNT[mask ? bytes[i] & mask[i] : bytes[i]];
    return count;
};

/** Calls fn(idx) for every set bit of one level. */
export const forEachSetBit = (bytes, entry, fn) => {
    const end = entry.byteOffset + Math.ceil(entry.bitCount / 8);
    for (let i = entry.byteOffset; i < end; i++) {
        const byte = bytes[i];
        if (!byte) continue;
        for (let bit = 0; bit < 8; bit++) {
            if (byte & (0x80 >> bit)) fn(((i - entry.byteOffset) << 3) + bit);
        }
    }
};
